'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/clients/supabase/supabaseBrowserClient';
import { User, AuthError } from '@supabase/supabase-js';
import { useRouter } from 'next/navigation';
import { getUser, signOut, signUpWithCredentials } from '@/lib/actions/auth';
import { APP_LINKS } from '@/lib/constants';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  signUpWithCredentials: ({
    email,
    password,
    username,
  }: {
    email: string;
    password: string;
    username: string;
  }) => Promise<any>;
  signInWithCredentials: ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => Promise<any>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<{ error: AuthError | null } | undefined>;
  getUser: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isLoading: true,
  signUpWithCredentials: async () => {},
  signInWithCredentials: async () => {},
  signInWithGoogle: async () => {},
  signOut: async () => ({ error: null }),
  getUser: async () => null,
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const router = useRouter();

  // Auth helper functions
  const _signUpWithCredentials = async ({
    email,
    password,
    username,
  }: {
    email: string;
    password: string;
    username: string;
  }) => {
    return await signUpWithCredentials({
      email,
      password,
      username,
    });
  };

  const _signOut = async () => {
    try {
      await signOut();
      setUser(null);
      router.push(APP_LINKS.HOME);
      return { error: null };
    } catch (error) {
      console.error('Error signing out:', error);

      return { error: error as AuthError };
    }
  };

  const _getUser = async () => {
    try {
      const {
        data: { session },
      } = await createClient().auth.getSession();

      if (!session) {
        setUser(null);
        return null;
      }

      return await getUser();
    } catch (error) {
      console.log('Error fetching user:', error);
      return null;
    }
  };

  const _signInWithCredentials = async ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    const supabase = createClient();
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { data, error };
  };

  const _signInWithGoogle = async () => {
    const supabase = createClient();
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}`,
      },
    });
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = await _getUser();

        setUser(user ?? null);
      } catch (error) {
        console.error('Error fetching session:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();

    // Set up auth state change listener
    const {
      data: { subscription },
    } = createClient().auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null);
      setIsLoading(false);

      switch (event) {
        case 'SIGNED_IN':
          break;
        case 'SIGNED_OUT':
          // Reset the sign-out flag and ensure user is null
          setUser(null);
          break;
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [router]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        signUpWithCredentials: _signUpWithCredentials,
        signInWithCredentials: _signInWithCredentials,
        signInWithGoogle: _signInWithGoogle,
        signOut: _signOut,
        getUser: _getUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
